import { useEffect, useRef, useState } from 'react';
import type {
  WarehouseHeroAction,
  WarehouseHeroResult,
} from '../interfaces/warehouseHero';

const labels: Record<string, string> = {
  synced: 'Abgeglichen',
  pending: 'Übertragung ausstehend',
  unknown: 'Ergebnis noch unklar',
  conflict: 'Abweichung prüfen',
  error: 'HERO nicht lesbar',
  unverified: 'Noch nicht geprüft',
};

export default function WarehouseHeroStatus({
  articleId,
  unit,
  status,
  disabled,
  run,
}: {
  articleId: string;
  unit: string;
  status?: string;
  disabled: boolean;
  run: WarehouseHeroAction;
}) {
  const [result, setResult] = useState<WarehouseHeroResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [physical, setPhysical] = useState(false);
  const alive = useRef(true);
  const sending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  async function act(mode: 'check' | 'sync' | 'reconcile') {
    if (disabled || sending.current) return;
    if (mode === 'reconcile' && (!physical || !reason.trim())) return;
    sending.current = true;
    setBusy(true);
    setError('');
    try {
      const next =
        mode === 'reconcile'
          ? await run(articleId, mode, {
              requestId: crypto.randomUUID(),
              reason: reason.trim(),
              physicalConfirmed: physical,
            })
          : await run(articleId, mode);
      if (alive.current) setResult(next);
    } catch {
      if (alive.current) {
        setResult(null);
        setError(
          'Abgleich nicht bestätigt. Verbindung und Zugriffsrechte prüfen. Danach zuerst erneut prüfen.',
        );
      }
    } finally {
      sending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  return (
    <details className="wh-hero-status">
      <summary>
        HERO-Abgleich ·{' '}
        {labels[result?.status ?? status ?? 'unverified'] ?? 'Prüfung nötig'}
      </summary>
      <p>
        Nur der Gesamtbestand wird mit HERO abgeglichen. Lagerorte bleiben in
        360.
      </p>
      {result && (
        <div role="status">
          <p>
            360: {result.target} {unit} · HERO zuletzt gelesen:{' '}
            {result.observedTotal === null
              ? 'unbekannt'
              : `${result.observedTotal} ${unit}`}
          </p>
          {result.message && <p>{result.message}</p>}
          {result.status === 'synced' && (
            <p>Beide Bestände waren bei dieser Prüfung gleich.</p>
          )}
        </div>
      )}
      {error && <p role="alert">{error}</p>}
      {result?.canReconcile && (
        <div>
          <p>
            Beide Gesamtbestände sind wieder gleich. Bestätige die Klärung erst
            nach Prüfung der Lagerorte und des Lagerbuchs.
          </p>
          <label>
            Grund der Klärung
            <input
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={physical}
              onChange={(e) => setPhysical(e.target.checked)}
            />
            Bestand gezählt und Lagerorte geprüft
          </label>
          <button
            disabled={disabled || busy || !physical || !reason.trim()}
            onClick={() => void act('reconcile')}
          >
            Geprüfte Bestände bestätigen
          </button>
        </div>
      )}
      <button disabled={disabled || busy} onClick={() => void act('check')}>
        {busy ? 'Wird geprüft …' : 'HERO-Bestand prüfen'}
      </button>
      {result?.status === 'pending' && result.canWrite && (
        <button
          className="primary"
          disabled={disabled || busy}
          onClick={() => void act('sync')}
        >
          Gesamtbestand an HERO übertragen
        </button>
      )}
      {disabled && (
        <p className="wh-field-hint">
          Mit Verbindung und ohne laufende Buchung erneut prüfen.
        </p>
      )}
    </details>
  );
}
